import cx from 'classnames'
import React from 'react'

import { Primary } from '@cinch/oxd-components/button/src'
import Image from '@cinch/oxd-components/image/src'
import { Text, TextStyles } from '@cinch-labs/design-system'
import { Env, getEnv } from '@cinch/oxd-utils/env/src'
import { getDefaultThumbnailSrc, getMake, getModel } from '@cinch/oxd-utils/manufacturers/src'

import * as styles from './recommended-card.module.css'

import * as skeletonStyles from './skeleton.module.css'

interface VehicleDetails {
  make: string
  model: string
  bodyType: string
  fuelType: string
  imagePath: string
  score: number
}

export interface RecommendedCardProps {
  vehicleDetails: VehicleDetails
  isPending: boolean
  buttonText: string
  onClick: any
}

const STATIC_ASSETS_URL = getEnv(Env.StaticAssetsUrl)
const LOADING_TEXT = 'Loading...'
const ERROR_MESSAGE = 'NO IMAGE'
const DEFAULT_IMAGE = getDefaultThumbnailSrc()

interface Title {
  isPending: boolean
}

const Title: React.FC<Title> = ({ children, isPending = false }) => (
  <Text
    oxford={true}
    element="h5"
    className={cx(skeletonStyles.base, styles.metaTitle, { [skeletonStyles.active]: isPending })}
    type={TextStyles.mainTitle}
    children={isPending ? LOADING_TEXT : children}
  />
)

const Subtitle: React.FC<Title> = ({ children, isPending = false }) => (
  <p
    className={cx(styles.subtitle, skeletonStyles.base, { [skeletonStyles.active]: isPending })}
    children={isPending ? LOADING_TEXT : children}
  />
)

const RecommendedCard: React.FC<RecommendedCardProps> = ({
  vehicleDetails,
  isPending = false,
  buttonText,
  onClick,
}) => {
  const { make, model, fuelType, bodyType, imagePath } = vehicleDetails

  return (
    <div data-testid="recommendation" className={styles.recommendedCard}>
      <div className={styles.imageWrapper}>
        <Image
          className={styles.image}
          src={`${STATIC_ASSETS_URL}/recommended/${imagePath}`}
          alt={`${make} ${model} - ${fuelType} ${bodyType}`}
          isLazy={true}
          i18n={{ noImage: ERROR_MESSAGE }}
          onError={(e) => (e.target.src = DEFAULT_IMAGE)}
        />
      </div>
      <div className={styles.meta}>
        <div>
          <Title isPending={isPending}>{`${getMake(make)} ${getModel(make, model)}`}</Title>
          <Subtitle isPending={isPending}>
            {fuelType} {bodyType}
          </Subtitle>
        </div>
        <Primary
          type="button"
          oxdClassName={styles.recommendedAction}
          onClick={() => onClick(vehicleDetails)}
          oxford={true}
        >
          {buttonText}
        </Primary>
      </div>
    </div>
  )
}

export default RecommendedCard
